// Cycle Count - Count Items
import React, { useState, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft,
  ArrowRight,
  BarChart3,
  Search,
  X,
  CheckCircle2,
  AlertCircle,
  Package,
  Filter,
  Save,
} from "lucide-react";
import { useNavigation } from "@/components/layout/app-layout";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";

// Mock stock items data for cycle count (ABC-based or manual selection)
const mockStockItems = [
  { code: "STK001", description: "Laptop Dell XPS 15", category: "Electronics", abcClass: "A", location: "A01-R01", systemQty: 45, unit: "Each", value: 1200, lastCycleCount: "2024-11-15" },
  { code: "STK002", description: "Office Chair Ergonomic", category: "Furniture", abcClass: "B", location: "A01-R02", systemQty: 120, unit: "Each", value: 250, lastCycleCount: "2024-10-20" },
  { code: "STK003", description: "A4 Paper Ream (500 sheets)", category: "Stationery", abcClass: "C", location: "B01-R01", systemQty: 350, unit: "Ream", value: 5, lastCycleCount: "2024-09-10" },
  { code: "STK004", description: "Steel Rods 10mm x 6m", category: "Raw Materials", abcClass: "A", location: "A02-R01", systemQty: 89, unit: "Piece", value: 45, lastCycleCount: "2024-11-18" },
  { code: "STK005", description: "Wireless Mouse Logitech", category: "Electronics", abcClass: "B", location: "B01-R02", systemQty: 200, unit: "Each", value: 25, lastCycleCount: "2024-10-25" },
  { code: "STK006", description: "Standing Desk Adjustable", category: "Furniture", abcClass: "A", location: "A01-R03", systemQty: 35, unit: "Each", value: 450, lastCycleCount: "2024-11-20" },
  { code: "STK007", description: "Ballpoint Pens Blue (Box of 50)", category: "Stationery", abcClass: "C", location: "B01-R03", systemQty: 180, unit: "Box", value: 8, lastCycleCount: "2024-08-15" },
  { code: "STK008", description: "Aluminum Sheets 2mm", category: "Raw Materials", abcClass: "B", location: "A02-R02", systemQty: 67, unit: "Sheet", value: 75, lastCycleCount: "2024-10-30" },
  { code: "STK009", description: "Monitor 27\" 4K Dell", category: "Electronics", abcClass: "A", location: "B02-R01", systemQty: 78, unit: "Each", value: 400, lastCycleCount: "2024-11-22" },
  { code: "STK010", description: "Filing Cabinet 4-Drawer", category: "Furniture", abcClass: "B", location: "A01-R04", systemQty: 42, unit: "Each", value: 180, lastCycleCount: "2024-10-18" },
];

interface CountItem {
  code: string;
  description: string;
  category: string;
  abcClass: string;
  location: string;
  systemQty: number;
  unit: string;
  value: number;
  lastCycleCount: string;
  countedQty: string;
  countedBy: string;
  notes: string;
  isCounted: boolean;
}

interface CycleCountPageProps {
  setupData?: {
    countType: "abc-based" | "manual";
    formData: {
      date: Date;
      warehouse: string;
      assignedTo: string;
      description: string;
      notes: string;
      abcClass?: string; // For ABC-based counts
    };
  };
}

export function StockTakesCycleCountCountPage({ setupData }: CycleCountPageProps) {
  const { navigateTo } = useNavigation();
  
  // Initialize count items based on setup data
  const initialItems: CountItem[] = useMemo(() => {
    let filteredItems = mockStockItems;
    
    // Filter by ABC class if ABC-based count
    if (setupData?.countType === "abc-based" && setupData.formData.abcClass) {
      filteredItems = filteredItems.filter(item => item.abcClass === setupData.formData.abcClass);
    }
    
    return filteredItems.map(item => ({
      ...item,
      countedQty: "",
      countedBy: setupData?.formData.assignedTo || "",
      notes: "",
      isCounted: false,
    }));
  }, [setupData]);

  const [countItems, setCountItems] = useState<CountItem[]>(initialItems);
  const [searchQuery, setSearchQuery] = useState("");
  const [showCountedOnly, setShowCountedOnly] = useState(false);
  const [showUnCountedOnly, setShowUnCountedOnly] = useState(false);
  const [showDiscrepanciesOnly, setShowDiscrepanciesOnly] = useState(false);

  // Filter items based on search and filters
  const filteredItems = useMemo(() => {
    return countItems.filter(item => {
      // Search filter
      const matchesSearch = searchQuery === "" || 
        item.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.location.toLowerCase().includes(searchQuery.toLowerCase());

      // Status filters
      if (showCountedOnly && !item.isCounted) return false;
      if (showUnCountedOnly && item.isCounted) return false;
      if (showDiscrepanciesOnly) {
        if (!item.isCounted) return false;
        const countedQty = parseFloat(item.countedQty) || 0;
        if (countedQty === item.systemQty) return false;
      }

      return matchesSearch;
    });
  }, [countItems, searchQuery, showCountedOnly, showUnCountedOnly, showDiscrepanciesOnly]);

  // Calculate statistics
  const stats = useMemo(() => {
    const totalItems = countItems.length;
    const countedItems = countItems.filter(item => item.isCounted).length;
    const discrepancies = countItems.filter(item => {
      if (!item.isCounted) return false;
      const countedQty = parseFloat(item.countedQty) || 0;
      return countedQty !== item.systemQty;
    }).length;
    const progress = totalItems > 0 ? Math.round((countedItems / totalItems) * 100) : 0;

    return { totalItems, countedItems, discrepancies, progress };
  }, [countItems]);

  const handleCountChange = (code: string, value: string) => {
    setCountItems(prev => prev.map(item => 
      item.code === code 
        ? { ...item, countedQty: value, isCounted: value !== "" }
        : item
    ));
  };

  const handleNotesChange = (code: string, value: string) => {
    setCountItems(prev => prev.map(item => 
      item.code === code 
        ? { ...item, notes: value }
        : item
    ));
  };

  const handleSaveDraft = () => {
    toast.success("Draft saved successfully");
  };

  const handleContinueToReview = () => {
    const uncountedItems = countItems.filter(item => !item.isCounted);
    
    if (uncountedItems.length > 0) {
      toast.warning(`${uncountedItems.length} items have not been counted yet`);
      return;
    }

    navigateTo("stock-takes/cycle-count/review", {
      setupData,
      countedItems: countItems,
    });
  };

  const getVarianceColor = (systemQty: number, countedQty: string) => {
    if (!countedQty) return "";
    const counted = parseFloat(countedQty) || 0;
    const variance = counted - systemQty;
    if (variance === 0) return "text-green-600 dark:text-green-400";
    if (variance > 0) return "text-blue-600 dark:text-blue-400";
    return "text-red-600 dark:text-red-400";
  };

  const getVarianceText = (systemQty: number, countedQty: string) => {
    if (!countedQty) return "";
    const counted = parseFloat(countedQty) || 0;
    const variance = counted - systemQty;
    if (variance === 0) return "Match";
    if (variance > 0) return `+${variance}`;
    return `${variance}`;
  };

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      {/* Fixed Header */}
      <div className="flex-shrink-0 pb-4 border-b bg-background">
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigateTo("stock-takes/cycle-count/setup")}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Setup
          </Button>
        </div>

        {/* Title Section */}
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-lg bg-orange-50 dark:bg-orange-900/20">
            <BarChart3 className="h-6 w-6 text-orange-600 dark:text-orange-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Count Items</h1>
            <p className="text-muted-foreground">
              {setupData?.countType === "abc-based" 
                ? `ABC Class ${setupData.formData.abcClass} Cycle Count`
                : "Manual Cycle Count"}
            </p>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto py-6 space-y-6">
        {/* Progress Card */}
        <Card>
        <CardHeader>
          <CardTitle>Counting Progress</CardTitle>
          <CardDescription>Track your cycle count progress</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Progress Bar */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Overall Progress</span>
                <span className="text-sm font-medium">{stats.progress}%</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                <div
                  className="bg-orange-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${stats.progress}%` }}
                />
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="text-2xl font-bold">{stats.totalItems}</div>
                <div className="text-xs text-muted-foreground">Total Items</div>
              </div>
              <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.countedItems}</div>
                <div className="text-xs text-muted-foreground">Counted</div>
              </div>
              <div className="text-center p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">{stats.discrepancies}</div>
                <div className="text-xs text-muted-foreground">Discrepancies</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search and Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by code, description, or location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-10"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 p-0"
                  onClick={() => setSearchQuery("")}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>

            {/* Filter Checkboxes */}
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="showCounted"
                  checked={showCountedOnly}
                  onCheckedChange={(checked) => {
                    setShowCountedOnly(checked as boolean);
                    if (checked) {
                      setShowUnCountedOnly(false);
                      setShowDiscrepanciesOnly(false);
                    }
                  }}
                />
                <Label htmlFor="showCounted" className="text-sm cursor-pointer">
                  Show counted only
                </Label>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="showUncounted"
                  checked={showUnCountedOnly}
                  onCheckedChange={(checked) => {
                    setShowUnCountedOnly(checked as boolean);
                    if (checked) {
                      setShowCountedOnly(false);
                      setShowDiscrepanciesOnly(false);
                    }
                  }}
                />
                <Label htmlFor="showUncounted" className="text-sm cursor-pointer">
                  Show uncounted only
                </Label>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="showDiscrepancies"
                  checked={showDiscrepanciesOnly}
                  onCheckedChange={(checked) => {
                    setShowDiscrepanciesOnly(checked as boolean);
                    if (checked) {
                      setShowCountedOnly(false);
                      setShowUnCountedOnly(false);
                    }
                  }}
                />
                <Label htmlFor="showDiscrepancies" className="text-sm cursor-pointer">
                  Show discrepancies only
                </Label>
              </div>
            </div>

            {/* Results count */}
            <div className="text-sm text-muted-foreground">
              Showing {filteredItems.length} of {countItems.length} items
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Count Table */}
      <Card className="flex-1 overflow-hidden flex flex-col">
        <CardHeader>
          <CardTitle>Items to Count</CardTitle>
          <CardDescription>Enter the physical count for each item</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[100px]">Status</TableHead>
                <TableHead className="w-[120px]">Code</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="w-[100px]">ABC Class</TableHead>
                <TableHead className="w-[100px]">Location</TableHead>
                <TableHead className="w-[100px] text-right">System Qty</TableHead>
                <TableHead className="w-[120px] text-right">Counted Qty</TableHead>
                <TableHead className="w-[100px] text-right">Variance</TableHead>
                <TableHead className="w-[200px]">Notes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    No items found matching your filters
                  </TableCell>
                </TableRow>
              ) : (
                filteredItems.map((item) => (
                  <TableRow key={item.code}>
                    <TableCell>
                      {item.isCounted ? (
                        <Badge variant="default" className="bg-green-600">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Counted
                        </Badge>
                      ) : (
                        <Badge variant="secondary">
                          <Package className="h-3 w-3 mr-1" />
                          Pending
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">{item.code}</TableCell>
                    <TableCell>{item.description}</TableCell>
                    <TableCell>
                      <Badge 
                        variant="outline"
                        className={
                          item.abcClass === "A" 
                            ? "border-red-500 text-red-700 dark:text-red-400"
                            : item.abcClass === "B"
                            ? "border-yellow-500 text-yellow-700 dark:text-yellow-400"
                            : "border-green-500 text-green-700 dark:text-green-400"
                        }
                      >
                        Class {item.abcClass}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{item.location}</TableCell>
                    <TableCell className="text-right font-medium">
                      {item.systemQty} {item.unit}
                    </TableCell>
                    <TableCell className="text-right">
                      <Input
                        type="number"
                        placeholder="0"
                        value={item.countedQty}
                        onChange={(e) => handleCountChange(item.code, e.target.value)}
                        className="w-24 text-right ml-auto"
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      {item.countedQty && (
                        <span className={`font-medium ${getVarianceColor(item.systemQty, item.countedQty)}`}>
                          {getVarianceText(item.systemQty, item.countedQty)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Input
                        placeholder="Add notes..."
                        value={item.notes}
                        onChange={(e) => handleNotesChange(item.code, e.target.value)}
                        className="w-full"
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      </div>

      {/* Fixed Action Bar */}
      <div className="flex-shrink-0 border-t bg-background shadow-lg p-4">
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => navigateTo("stock-takes/cycle-count/setup")}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Setup
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleSaveDraft} className="gap-2">
              <Save className="h-4 w-4" />
              Save Draft
            </Button>
            <Button onClick={handleContinueToReview} className="gap-2">
              Continue to Review
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
